import { isEmpty } from 'lodash';
import { useContext } from 'react';

import { Branding } from '../../common/interfaces/branding/branding';
import { config } from '../../config';
import { DynamicBrandingContext } from '../../providers/context/DynamicBrandingProvider/DynamicBrandingContext';
import { setDynamicAppInfo } from '../../utils/setDynamicAppInfo/setDynamicAppInfo';
import { useAPI } from '../useAPI/useAPI';

/**
 * Hook for managing application branding.
 *
 * Branding Strategy:
 * - Default: All styling (colors, logos, theme) comes from Contentful
 * - Lead Channel: Uses default styling BUT overrides `id` and `brandName`
 *   - `id`: Determines which email template to use
 *   - `brandName`: Displayed in terms of use agreement
 */
export const useBranding = (): {
  initializeBranding: (leadChannelCode: string | null) => Promise<void>;
  branding: Branding;
  failedToRetrieveBranding: boolean;
  isLoading: boolean;
} => {
  const api = useAPI();
  const {
    branding: currentBranding,
    changeBrandingState,
    failedToRetrieveBranding,
    isLoading,
  } = useContext(DynamicBrandingContext);

  const fetchDefaultBranding = async (): Promise<Branding> => {
    const result: Branding = await api.get('/ui-configuration/branding');

    // If no default branding found, return current branding (hardcoded) as fallback
    if (isEmpty(result)) {
      return currentBranding;
    }

    return result;
  };

  const fetchLeadChannelBranding = async (
    code: string,
  ): Promise<Branding | null> => {
    const result: Branding = await api.get(
      `/ui-configuration/branding?leadChannelCode=${code}`,
    );

    if (isEmpty(result)) {
      return null;
    }

    return result;
  };

  const overrideIdentityFields = (
    styling: Branding,
    identity: Branding,
  ): Branding => ({
    ...styling,
    id: identity.id,
    brandName: identity.brandName,
  });

  const setIdToDefault = (brandingConfig: Branding): Branding => ({
    ...brandingConfig,
    id: config.branding.defaultId,
  });

  const applyBrandingToState = (brandingConfig: Branding): void => {
    changeBrandingState({
      branding: brandingConfig,
      isLoading: false,
    });
    setDynamicAppInfo(brandingConfig);
  };

  const applyBrandingToStateWithFailure = (brandingConfig: Branding): void => {
    changeBrandingState({
      branding: brandingConfig,
      isLoading: false,
      failedToRetrieveBranding: true,
    });
    setDynamicAppInfo(brandingConfig);
  };

  const initializeBranding = async (
    leadChannelCode: string | null,
  ): Promise<void> => {
    try {
      const defaultBranding = await fetchDefaultBranding();

      // No lead channel - use default branding as-is
      if (!leadChannelCode) {
        applyBrandingToState(defaultBranding);
        return;
      }

      // Lead channel provided - fetch it
      const leadChannelBranding = await fetchLeadChannelBranding(
        leadChannelCode,
      );

      // Lead channel not found - use default styling with default ID
      if (!leadChannelBranding) {
        const brandingWithDefaultId = setIdToDefault(defaultBranding);

        applyBrandingToStateWithFailure(brandingWithDefaultId);

        return;
      }

      // Lead channel found - use default styling with lead channel identity
      const brandingWithLeadChannelIdentity = overrideIdentityFields(
        defaultBranding,
        leadChannelBranding,
      );

      applyBrandingToState(brandingWithLeadChannelIdentity);
    } catch (error: unknown) {
      console.warn('Failed to load branding, using fallback', error);
      applyBrandingToState(currentBranding);
    }
  };

  return {
    initializeBranding,
    branding: currentBranding,
    failedToRetrieveBranding,
    isLoading,
  };
};
